import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

const ProfileContext = createContext();

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfile must be used within ProfileProvider');
    }
    return context;
};

export const ProfileProvider = ({ children }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageVersion, setImageVersion] = useState(Date.now()); // Cache buster

    const fetchProfile = useCallback(async () => {
        // Only fetch profile if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/api/user/profile');
            setProfile(response.data);
            setImageVersion(Date.now()); // Update cache buster
        } catch (error) {
            // Only log error if it's not a 401/403 (unauthenticated)
            if (error.response?.status !== 401 && error.response?.status !== 403) {
                console.error('Failed to fetch profile:', error);
            }
            // Clear token if unauthorized
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                setProfile(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const updateProfileImage = useCallback(async (imageUrl) => {
        // Optimistic update - update UI immediately
        setProfile(prev => ({
            ...prev,
            profile_image_url: imageUrl
        }));

        // Force cache bust for all profile images
        setImageVersion(Date.now());

        // Fetch fresh profile data in background to ensure consistency
        await fetchProfile();
    }, [fetchProfile]);

    const updateProfile = useCallback((updates) => {
        setProfile(prev => ({
            ...prev,
            ...updates
        }));
    }, []);

    return (
        <ProfileContext.Provider
            value={{
                profile,
                loading,
                imageVersion,
                updateProfileImage,
                updateProfile,
                fetchProfile
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
};